// src/components/printers/PrinterDiscovery.jsx
import React, { useState } from 'react';
import { useDiscoveredPrinters } from '../../../hooks/usePrinters';
import ImageWithLoader from '../../common/ImageWithLoader';
import '../../../Css/PrinterDiscovery.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function PrinterDiscovery({ hubUuid, onPrinterSelected }) {
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  
  // Funkcja do tworzenia URL ikony na podstawie modelu
  const getIconUrl = (printer) => {
    // Extract model from printer data (usually in 'model' or 'dev_model_name')
    const model = printer.model || printer.dev_model_name || 'unknown';
    const brand = 'bambulab'; // All BambuLab printers
    return `${API_URL}/api/icons/printers/${brand}/${model}/preview.webp`;
  };
  
  // Używamy hooka do pobierania drukarek (tylko synchroniczne API)
  const { 
    data, // Zmieniono z data: printers na data 
    isLoading, 
    error,
    refetch
  } = useDiscoveredPrinters(hubUuid);
  
  // Konwertuj obiekt drukarek na tablicę do wyświetlenia
  const availablePrintersList = data?.available_printers ? 
    Object.entries(data.available_printers).map(([serialNumber, printerData]) => ({
      serialNumber,
      ...printerData
    })) : [];
    
  const connectedPrintersList = data?.connected_printers ? 
    Object.entries(data.connected_printers).map(([serialNumber, printerData]) => ({
      serialNumber,
      ...printerData
    })) : [];

  // Filtruj tylko drukarki online
  const availablePrinters = availablePrintersList.filter(printer => 
    printer.status === "online"
  );
  
  const connectedPrinters = connectedPrintersList.filter(printer => 
    printer.status === "online"
  );
  
  // Funkcja do ręcznego odświeżenia listy
  const handleRefresh = () => {
    refetch();
  };

  // Funkcja do wyboru drukarki - blokuj cloud drukarki
  const handleSelectPrinter = (printer) => {
    // Blokuj wybór cloud drukarek
    if (printer.connection_type === 'cloud') {
      console.log('Cloud printer selection blocked:', printer.device_name);
      return;
    }
    
    setSelectedPrinter(printer);
    if (onPrinterSelected) {
      onPrinterSelected(printer);
    }
  };
  
  const handleTransferPrinter = (printer) => {
    // TODO: Implementować transfer drukarki między hubami
    console.log('Transfer printer:', printer);
    alert(`Transfer funkcjonalność będzie dostępna wkrótce!\nDrukarka: ${printer.device_name}\nAktualny hub: ${printer.connection_info?.hub_name}`);
  };
  
  if (isLoading) {
    return (
      <div className="loading-container">
        <p>Pobieranie listy drukarek...</p>
      </div>
    );
  }
  
  return (
    <div className="printer-discovery">
      <div className="header-with-refresh">
        <h3>Wykryte drukarki</h3>
        <button onClick={handleRefresh} disabled={isLoading} className="refresh-button">
          {isLoading ? 'Odświeżanie...' : 'Odśwież'}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          Błąd podczas pobierania drukarek: {error.message}
        </div>
      )}
      
      {/* Statystyki drukarek */}
      {data?.printer_stats && (
        <div className="printer-stats">
          <p className="stats-text">
            📊 Znaleziono {data.printer_stats.total_count} drukarek w sieci: 
            {data.printer_stats.available_count} dostępnych, 
            {data.printer_stats.connected_count} już podłączonych
          </p>
        </div>
      )}
      
      {(availablePrinters.length === 0 && connectedPrinters.length === 0) ? (
        <div className="no-printers-message">
          <p>Nie wykryto żadnych dostępnych drukarek.</p>
          <p>Upewnij się, że drukarki są włączone i podłączone do sieci.</p>
          <button onClick={handleRefresh} className="retry-button">
            Spróbuj ponownie
          </button>
        </div>
      ) : (
        <div className="printer-sections">
          
          {/* Sekcja dostępnych drukarek */}
          {availablePrinters.length > 0 && (
            <div className="printer-section available-section">
              <h3 className="section-title">
                🆕 Dostępne do dodania ({availablePrinters.length})
              </h3>
              <p className="section-description">
                Wybierz drukarkę, którą chcesz dodać do systemu:
              </p>
              <div className="printer-grid">
                {availablePrinters.map(printer => {
                  const isCloud = printer.connection_type === 'cloud';
                  const isSelected = selectedPrinter?.serialNumber === printer.serialNumber;
                  
                  return (
                    <div 
                      key={printer.serialNumber} 
                      className={`printer-card available ${isSelected ? 'selected' : ''} ${isCloud ? 'cloud' : ''}`}
                      onClick={() => handleSelectPrinter(printer)}
                      title={isCloud ? 'Cloud drukarki nie są obsługiwane - tylko drukarki LAN' : 'Kliknij aby wybrać'}
                    >
                    <div className="printer-icon-container">
                      <ImageWithLoader
                        src={getIconUrl(printer)}
                        alt={`${printer.device_name} icon`}
                        className="printer-icon"
                        fallbackText={printer.device_name.charAt(0).toUpperCase()}
                        iconType="printer"
                        size="preview"
                      />
                    </div>
                    <div className="printer-info">
                      <h4 className="printer-name">{printer.device_name}</h4>
                      <p className="printer-model">{printer.model}</p>
                      <p className="printer-ip">IP: {printer.ip}</p>
                      <div className="printer-status">
                        <span className="status-indicator online"></span>
                        <span className="status-text">{printer.status}</span>
                      </div>
                      <div className="printer-connection">
                        <span className={`connection-type connection-${printer.connection_type}`}>
                          {printer.connection_type === 'cloud' ? 'Cloud' : 'LAN'}
                        </span>
                      </div>
                    </div>
                    <div className="select-overlay">
                      <span className="select-text">Dodaj drukarkę</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Sekcja już podłączonych drukarek */}
          {connectedPrinters.length > 0 && (
            <div className="printer-section connected-section">
              <h3 className="section-title">
                🔗 Już podłączone ({connectedPrinters.length})
              </h3>
              <p className="section-description">
                Te drukarki są już podłączone do innych hubów:
              </p>
              <div className="printer-grid">
                {connectedPrinters.map(printer => (
                  <div 
                    key={printer.serialNumber} 
                    className="printer-card connected"
                  >
                    <div className="printer-icon-container">
                      <ImageWithLoader
                        src={getIconUrl(printer)}
                        alt={`${printer.device_name} icon`}
                        className="printer-icon"
                        fallbackText={printer.device_name.charAt(0).toUpperCase()}
                        iconType="printer"
                        size="preview"
                      />
                    </div>
                    <div className="printer-info">
                      <h4 className="printer-name">{printer.device_name}</h4>
                      <p className="printer-model">{printer.model}</p>
                      <p className="printer-ip">IP: {printer.ip}</p>
                      <div className="printer-status">
                        <span className="status-indicator online"></span>
                        <span className="status-text">{printer.status}</span>
                      </div>
                      <div className="connection-info">
                        <p className="connected-to">
                          📡 Podłączona do: <strong>{printer.connection_info?.hub_name}</strong>
                        </p>
                        <p className="connection-date">
                          📅 Dodana: {new Date(printer.connection_info?.date_added).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="connected-overlay">
                      <span className="connected-text">Już podłączona</span>
                      <button 
                        className="transfer-button"
                        onClick={() => handleTransferPrinter(printer)}
                        title="Przenieś na ten hub"
                      >
                        🔄 Przenieś tutaj
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {selectedPrinter && (
            <div className="selected-printer-info">
              <h4>Wybrana drukarka:</h4>
              <p><strong>{selectedPrinter.device_name}</strong> ({selectedPrinter.model})</p>
              <p>Adres IP: {selectedPrinter.ip}</p>
              <p>Typ połączenia: <span className={`connection-type connection-${selectedPrinter.connection_type}`}>
                {selectedPrinter.connection_type === 'cloud' ? 'Cloud' : 'LAN'}
              </span></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}